import { type Location } from '@koala/sdk';
import { useEffect, useState } from 'react';
import {
  getStoreAvailabilityState,
  isPosUnavailable,
  STORE_AVAILABILITY_STATE,
} from '@/utils/locations';

/**
 * Schedules a single re-render at the POS-refusal expiry instant so consumers recompute
 * against the live clock and clear themselves the moment the 10-minute window lapses —
 * with no network refetch (the location payload is not polled). Satisfies AC3's "no deploy
 * and no cache flush". No-op when there is no active window.
 */
function usePosExpiryTick(until: string | null): void {
  const [, forceExpiryCheck] = useState(0);

  useEffect(() => {
    if (!until) {
      return;
    }

    const msUntilExpiry = new Date(until).getTime() - Date.now();
    if (!Number.isFinite(msUntilExpiry) || msUntilExpiry <= 0) {
      return;
    }

    const timer = setTimeout(() => forceExpiryCheck((value) => value + 1), msUntilExpiry);
    return () => clearTimeout(timer);
  }, [until]);
}

/**
 * True only after the component has mounted on the client. The availability reads use the
 * wall clock (`Date.now()`), which the server and client can disagree on around the expiry
 * second, so we defer to the client to avoid a React hydration mismatch (TEAM-14640).
 */
function useMounted(): boolean {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted;
}

/**
 * Reactive read of whether a location's POS is currently refusing orders (TEAM-14640).
 * POS-only — use for the submit gate and checkout notice, which should block regardless
 * of the higher-priority availability states. Returns false during SSR / the first render.
 */
export function usePosUnavailable(location?: Location): boolean {
  const mounted = useMounted();
  usePosExpiryTick(location?.pos_refused_until ?? null);
  return mounted && isPosUnavailable(location);
}

/**
 * Reactive read of a location's single highest-priority availability state (TEAM-14640),
 * respecting the design precedence closed-by-hours > operator-paused > POS-unavailable >
 * normal. Use where only one message may show at a time (the menu banner and modal).
 */
export function useStoreAvailabilityState(location?: Location): STORE_AVAILABILITY_STATE {
  const mounted = useMounted();
  usePosExpiryTick(location?.pos_refused_until ?? null);
  return mounted ? getStoreAvailabilityState(location) : STORE_AVAILABILITY_STATE.NORMAL;
}

/**
 * True when the store is in any unavailable state that should block the diner from ordering:
 * POS-unavailable (ten-minute refusal window) or operator-paused/disabled (`accepting_orders`
 * is false). Use for the checkout notice and submit gate, which should block regardless of
 * the higher-priority availability states (e.g. closed-by-hours) — same design as
 * `usePosUnavailable`, extended to cover operator-paused.
 *
 * Unlike `usePosUnavailable` (POS-only, self-clears at the refusal expiry via setTimeout),
 * the operator-paused state has no time-based auto-clear — it clears when the location is
 * next fetched with `accepting_orders: true` (a page reload or navigation). (TEAM-15703)
 */
export function useStoreUnavailable(location?: Location): boolean {
  const mounted = useMounted();
  usePosExpiryTick(location?.pos_refused_until ?? null);
  return (
    mounted &&
    (isPosUnavailable(location) ||
      location?.accepting_orders === false ||
      location?.provider_enabled === false)
  );
}
